import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { SplashScreen } from "@awesome-cordova-plugins/splash-screen/ngx";
import { StatusBar } from "@awesome-cordova-plugins/status-bar/ngx";
import { Platform } from "@ionic/angular";
import { AnalyticsService } from "./shared/analytics.service";
import { LangLevelPipe } from "./shared/pipes/lang-level.pipe";
import { PushService } from "./shared/push.service";
import { App } from '@capacitor/app';
import { AuthService } from "./shared/auth.service";
import { FirebaseInitService } from "./shared/firebase-init.service";
import { Badge } from "@capawesome/capacitor-badge";

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  providers: [ LangLevelPipe ]
})
export class AppComponent {

  constructor(
    private platform: Platform,
    private splashScreen: SplashScreen,
    private statusBar: StatusBar,
    public analytics: AnalyticsService,
    public readonly router: Router,
    public pushService: PushService,
    public authService: AuthService,
    private firebaseInit: FirebaseInitService
  ) {
    this.initializeApp();
  }

  async initializeApp() {
    this.platform.ready().then(async () => {
      try {
        console.log('Starting app initialization...');
        
        // Initialize Firebase first with proper error handling
        console.log('Initializing Firebase...');
        await this.initializeFirebase(); // Initialize Firebase for web first
        
        // Initialize Firebase services for both web and Capacitor
        console.log('Initializing Firebase services...');
        await this.firebaseInit.initialize(); // Initialize Firebase services
        
        console.log('Firebase initialization completed');
        
        this.statusBar.styleDefault();
        await this.browserActions();
        
        // Wait for Firebase to be fully ready before initializing push service
        console.log('Waiting for Firebase to be ready before initializing push service...');
        await this.firebaseInit.waitForReady();
        
        await this.pushService.init();
        if(this.platform.is("android"))
        {
          this.statusBar.overlaysWebView(true);
        }
        await this.analytics.init();
        
        // Handle redirect result for web Google Sign In
        if (!(this.platform.is('capacitor') || this.platform.is('cordova'))) {
          await this.authService.handleRedirectResult();
        }
        
        this.splashScreen.hide();
        
        console.log('App initialization completed successfully');
      } catch (error) {
        console.error('App initialization failed:', error);
        // Still hide splash screen even if initialization fails
        this.splashScreen.hide();
      }
    });
  }

  async browserActions() {
    App.addListener('appUrlOpen', (data: any) => {
      if (data && data.url) {
        try {
          const url = new URL(data.url);
          // Handle both 'grammario:' and 'grammario://' protocols
          if (url.protocol === 'grammario:' || data.url.startsWith('grammario://')) {
            console.log('📱 [AppComponent] Handling grammario deeplink');
            const code = url.searchParams.get('code');
            const state = url.searchParams.get('state');
            
            if (code && state) {
              this.authService.handleAuthCallback(code, state);
            } else {
              console.error('📱 [AppComponent] Missing code or state in callback');
            }
          }
        } catch (error) {
          console.error('📱 [AppComponent] Error parsing URL:', error);
          console.log('📱 [AppComponent] Raw URL:', data.url);
          
          // Fallback: Try to parse manually
          if (data.url.startsWith('grammario://callback')) {
            const urlParts = data.url.split('?');
            if (urlParts.length > 1) {
              const params = new URLSearchParams(urlParts[1]);
              const code = params.get('code');
              const state = params.get('state');
              console.log('📱 [AppComponent] Fallback parsing - code:', code ? 'present' : 'missing');
              if (code && state) {
                this.authService.handleAuthCallback(code, state);
              }
            }
          }
        }
      }
    });
    App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
          await Badge.clear();
          await Badge.set({count: 0});
      }
    });
  }

  public async initializeFirebase(): Promise<void> {
    if (this.platform.is('capacitor') || this.platform.is('cordova')) {
      // Firebase is initialized automatically by Capacitor plugins
      console.log('Capacitor platform detected, Firebase plugins should be auto-initialized');
      return;
    }
    // Only initialize Firebase for web platform
    try {
      console.log('Initializing Firebase for web platform...');
      
      // Note: Firebase is initialized via AngularFire providers in app.module.ts
      // No manual initialization needed for modular API
      console.log('Firebase is initialized via AngularFire providers');
    } catch (error) {
      console.error("Firebase initialization error:", error);
      throw error; // Re-throw to stop app initialization if Firebase fails
    }
  }
}
